import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $getState,
  $isTextNode,
  $setState,
  TextNode,
  type EditorState,
  type ElementNode,
  type LexicalNode,
} from 'lexical';
import { FC, useEffect, useLayoutEffect, useRef } from 'react';

import type { DocumentSectionSpec } from '../../../config';
import {
  $ensureTemplateLine,
  $isTemplateSubheading,
  createTemplateTextDef,
  createTempId,
} from '../../../lib';
import {
  $createTemplateParagraphNode,
  $isTemplateParagraphNode,
  TemplateHeadingNode,
  TemplateParagraphNode,
  templateSectionState,
  templateWordingState,
} from '../nodes';

// A field put into a text block cuts it in two: the part after the field
// is another block.
const $isCutOff = (
  node: TextNode,
  siblings: LexicalNode[],
  blockId: string,
) => {
  const index = siblings.findIndex((sibling) => sibling.is(node));
  const first = siblings.findIndex(
    (sibling) =>
      $isTextNode(sibling) &&
      $getState(sibling, templateWordingState)?.def.ID === blockId,
  );
  return siblings.slice(first, index).some((sibling) => !$isTextNode(sibling));
};

// Text that came into a line from another one (Enter cut it, Backspace
// joined it) or that a field cut off joins the text block it follows, or
// is a block of its own; so is text typed where no block was. A space
// between two fields is no block.
const $adoptText = (node: TextNode, committed: EditorState) => {
  const parent = node.getParent();
  if (
    !$isTemplateParagraphNode(parent) ||
    $joinAcrossRemovedBlock(node, committed)
  ) {
    return;
  }
  const { ID: lineId } = $ensureTemplateLine(parent);
  const wording = $getState(node, templateWordingState);
  const kept =
    wording?.lineId === lineId &&
    !$isCutOff(node, parent.getChildren(), wording.def.ID);
  if (kept) {
    return;
  }
  if (!node.getTextContent().trim()) {
    if (wording) {
      $setState(node, templateWordingState, null);
    }
    return;
  }
  const previous = node.getPreviousSibling();
  const before = $isTextNode(previous)
    ? $getState(previous, templateWordingState)
    : null;
  $setState(
    node,
    templateWordingState,
    before?.lineId === lineId
      ? before
      : { def: createTemplateTextDef(createTempId()), lineId },
  );
};

// A title or a subheading holds text alone: a field or a line break that
// comes into it, as when Backspace joins a line to it, goes on a line under
// it with what follows it.
const $keepTitleText = (title: ElementNode) => {
  const children = title.getChildren();
  const first = children.findIndex((child: LexicalNode) => !$isTextNode(child));
  if (first >= 0) {
    title.insertAfter(
      $createTemplateParagraphNode().append(...children.slice(first)),
    );
  }
};

// The text on either side of a field removed from its line is one text
// again: what stood after the field joins the block before it, so Lexical
// merges the two, and the space put around the field goes with it.
const $joinAcrossRemovedBlock = (node: TextNode, committed: EditorState) => {
  const before = node.getPreviousSibling();
  const wording = $getState(node, templateWordingState);
  const joined = $isTextNode(before)
    ? $getState(before, templateWordingState)
    : null;
  if (!$isTextNode(before) || !joined || joined === wording) {
    return false;
  }
  const stood = committed._nodeMap.get(node.getKey());
  const separated =
    !!stood &&
    !!stood.__prev &&
    stood.__prev !== before.getKey() &&
    committed._nodeMap.get(stood.__prev)?.getType() !== 'text';
  if (!separated) {
    return false;
  }
  if (/\s$/.test(before.getTextContent())) {
    node.setTextContent(node.getTextContent().replace(/^\s+/, ''));
  }
  $setState(node, templateWordingState, joined);
  return true;
};

type Props = {
  fields: boolean;
  // A section Lexical makes, as when Enter cuts a heading in two.
  createSection: (title: string) => DocumentSectionSpec;
};

// Every heading the document gets is a section, every paragraph a line, and
// every text node belongs to a text block of its line: the ids stay with
// what they name while the text is typed.
export const TemplateNodeStatePlugin: FC<Props> = ({
  fields,
  createSection,
}) => {
  const [editor] = useLexicalComposerContext();
  const create = useRef(createSection);

  useLayoutEffect(() => {
    create.current = createSection;
  });

  useEffect(
    () =>
      mergeRegister(
        editor.registerNodeTransform(TemplateHeadingNode, (heading) => {
          if (!$getState(heading, templateSectionState)) {
            $setState(heading, templateSectionState, {
              ...create.current(''),
              Lines: [],
            });
          }
          $keepTitleText(heading);
        }),
        ...(fields
          ? []
          : [
              editor.registerNodeTransform(TemplateParagraphNode, (line) => {
                $ensureTemplateLine(line);
                if ($isTemplateSubheading(line)) {
                  $keepTitleText(line);
                }
              }),
              editor.registerNodeTransform(TextNode, (node) =>
                $adoptText(node, editor.getEditorState()),
              ),
            ]),
      ),
    [editor, fields],
  );

  return null;
};
